import { Grid } from "@material-ui/core";
import { FC, useCallback, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AnySchema, mixed, object } from "yup";
import { FileField } from "../../../../../components/forms/file-3";
import { GridItem } from "../../../../../components/layout";
import { LaunchesDTO } from "../../../../launches/models/dto";
import { Field } from "../../internal/field";
import { ColumnWrapper, FieldHook, RenderFuncProps, Wrapper } from "../common";
import {
  methodsWithTwoSides,
  useIdentityDocumentAttachment,
} from "./attachment";
import { useIdentityDocumentDetails } from "./details";

const useSchema = () => {
  const { t } = useTranslation("fields");
  const { schema: attachmentSchema } = useIdentityDocumentAttachment();
  const { schema: detailsSchema } = useIdentityDocumentDetails();

  const additionalFieldSchema = useMemo(
    () =>
      mixed()
        .test(
          "is-large",
          t(`file-size-cannot-exceed-6-mb`),
          (file: any) => !(file instanceof File) || file.size <= 6 * 1024 * 1024
        )
        .when("attachment", {
          is: (
            x?: LaunchesDTO.Requests.ParticipationForm.T["identityDocument"]["attachment"]
          ) => x && methodsWithTwoSides.includes(x.type),
          then: (x) =>
            x.required(
              t("required-field", {
                ns: "errors",
                field: t("identity-document-additional-file"),
              })
            ),
          otherwise: (x) => x.notRequired(),
        })
        .label(t("identity-document-additional-file")),
    [t, methodsWithTwoSides]
  );

  return useCallback<(f: Field.DefaultField) => AnySchema>(
    (f: Field.DefaultField) =>
      object()
        .shape({
          attachment: attachmentSchema(f),
          additionalFile: additionalFieldSchema,
          details: detailsSchema,
        })
        .required(
          t("required-field", {
            ns: "errors",
            field: t("identity-document"),
          })
        )
        .label(t("identity-document")),
    [t, additionalFieldSchema, attachmentSchema, detailsSchema]
  );
};

const Elem: FC<RenderFuncProps> = ({ item }) => {
  const { control } = useFormContext();
  const { t } = useTranslation("fields");

  const additionalFileField = useController({
    control,
    name: `${item.field}.additionalFile`,
  });

  const { elem: IdentityDocumentAttachment } = useIdentityDocumentAttachment();
  const { elem: IdentityDocumentDetails } = useIdentityDocumentDetails();

  return (
    <Wrapper coloured>
      <Grid container spacing={3}>
        <GridItem top xs={12}>
          <strong>{t("proof-of-identity")}</strong>
        </GridItem>
        <IdentityDocumentAttachment
          path={`${item.field}.attachment`}
          item={item}
          additionalField={
            <ColumnWrapper>
              <FileField
                onChange={(x) => additionalFileField.field.onChange(x)}
                value={additionalFileField.field.value}
                error={!!additionalFileField.fieldState.error}
                label={t("back-side")}
                helperText={additionalFileField.fieldState.error?.message}
              />
            </ColumnWrapper>
          }
        />
        <IdentityDocumentDetails path={`${item.field}.details`} />
      </Grid>
    </Wrapper>
  );
};

export const useIdentityDocumentField: FieldHook = () => {
  const schema = useSchema();
  return useMemo(
    () => ({
      schema,
      elem: Elem,
      service: "DOCUMENT",
    }),
    [schema]
  );
};
